import React, { ReactNode } from "react";
import {
  AppBar,
  Box,
  Button,
  styled,
  Toolbar,
  Typography,
  useScrollTrigger,
  Link,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useToken } from "@rentablo/api";
import { useMandatorContext } from "@rentablo/theming";
import {
  HOME,
  YOUR_DISCOUNT,
  LOGIN,
  CASHBACK_WIZARD_START,
} from "@helpers/urlConstants";
import { Logo } from "@rentablo/components";
import { HeaderMobileMenu } from "./HeaderMobileMenu";
import { HeaderKnowledgeSubmenu } from "./HeaderKnowledgeSubmenu";

interface ScrolledProps {
  isScrolled: boolean;
  children: ReactNode;
}

const HeaderWrapper = styled(({ isScrolled, ...rest }: ScrolledProps) => (
  <AppBar {...rest} />
))(({ theme, isScrolled }) => ({
  display: "flex",
  padding: theme.spacing(2, 5),
  height: "65px",
  boxShadow: "none",
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.border.primary}`,

  [theme.breakpoints.down("md")]: {
    height: "58px",
    padding: theme.spacing(1, 3),
    borderBottom: isScrolled
      ? `1px solid ${theme.palette.border.primary}`
      : "none",
  },
}));

const ActionsWrapper = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",

  [theme.breakpoints.up("lg")]: {
    justifyContent: "space-between",
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(3),
}));

const LogoLink = styled(Link)(({ theme }) => ({
  display: "flex",
  alignItems: "center",

  [theme.breakpoints.up("lg")]: {
    marginRight: theme.spacing(11),
  },
  [theme.breakpoints.down(1325)]: {
    marginRight: theme.spacing(3),
  },
}));

const ActionsContainer = styled("div")({
  display: "flex",
  alignItems: "inherit",
  flexShrink: 0,
});

const MainMenuActions = styled(ActionsContainer)(({ theme }) => ({
  gap: theme.spacing(3),
  marginRight: theme.spacing(9),
  flexShrink: 0,

  [theme.breakpoints.down("lg")]: {
    display: "none",
  },

  [theme.breakpoints.down(1325)]: {
    gap: theme.spacing(0),
    marginRight: theme.spacing(3),
  },
}));

const ButtonsBlock = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}));

const NavigationButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(2, 4),
}));

const AuthenticationButton = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return !isLoggedIn ? (
    <LoginButton href={LOGIN} variant="outlined" size="small">
      {t("redesign.header.actions.login")}
    </LoginButton>
  ) : (
    <Button
      href={HOME}
      variant="primary"
      size="small"
      sx={{ marginRight: theme.spacing(3) }}
    >
      {t("redesign.header.actions.inApp")}
    </Button>
  );
};

export const ColumnsLayoutHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
  });
  const { isRentablo } = useMandatorContext();
  const { isSuccess: isTokenSuccess } = useToken();

  return (
    <HeaderWrapper isScrolled={trigger} data-sectionid="cashback-lp-header">
      <Toolbar variant="dense" disableGutters>
        <LogoLink href={HOME}>
          <Logo variant="agb" />
        </LogoLink>

        <ActionsWrapper>
          <MainMenuActions>
            {isRentablo && (
              <>
                <NavigationButton
                  href={`${import.meta.env.VITE_APP_ROOT_URL}/${YOUR_DISCOUNT}`}
                  size="small"
                >
                  <Typography color="textSecondary">
                    {t("redesign.header.actions.yourDiscount")}
                  </Typography>
                </NavigationButton>
                <HeaderKnowledgeSubmenu />
                {/*<NavigationButton href={ABOUT_US} size="small">*/}
                {/*  <Typography color="textSecondary">*/}
                {/*    {t("redesign.header.actions.aboutUs")}*/}
                {/*  </Typography>*/}
                {/*</NavigationButton>*/}
              </>
            )}
          </MainMenuActions>

          <ActionsContainer>
            <ButtonsBlock>
              <AuthenticationButton isLoggedIn={isTokenSuccess} />
              {isRentablo && (
                <Button
                  variant="primary"
                  color="cashback"
                  size="small"
                  data-eventid="book-cashback-button"
                  onClick={() => navigate(CASHBACK_WIZARD_START)}
                >
                  {t("redesign.header.actions.cashback")}
                </Button>
              )}
            </ButtonsBlock>

            <HeaderMobileMenu />
          </ActionsContainer>
        </ActionsWrapper>
      </Toolbar>
    </HeaderWrapper>
  );
};
